import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useDispatch, useSelector } from 'react-redux';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-phone-number-input/style.css';
import { useTranslation } from 'react-i18next';
import { selectors as sourceSelectors } from '../OrdersPage/reducer';
import { selectors as selectorsLogin } from '../LoginPage/reducer';
import * as actions from '../LoyaltyPage/actions';
import { selectors as loyaltySelectors } from '../LoyaltyPage/reducer';
import * as sourceActions from '../OrdersPage/actions';
import CurrentParametersTable from './components/CurrentParametersTable';
import NetworkSelector from './components/NetworkSelector';
import PeriodPickerPanel from './components/PeriodPickerPanel';
import SkuBonusManager from './components/SkuBonusManager';
import * as Styled from './LoyaltyPageReport.styles';
import { formatDataForLoyaltyReport } from '../LoyaltyPage/helpers';

import { ReportType } from './types';
import AdditionalBonuses from './components/AdditionalBonuses';

const ALL_SOURCES_OPTION = 'Все аптеки';

const CURRENT_PARAMETERS = [
  { label: 'Интеграционный код', value: 'фывфвыфвы' },
  { label: 'Базовый бонус', value: '1%' },
  { label: 'Максимальный процент оплаты бонусами', value: '100%' },
  { label: 'Бонусы фармацевта', value: '100' },
];
const LoyaltyPageReport = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const dataUser = useSelector(selectorsLogin.userData);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [errorTooltip, setErrorTooltip] = useState<boolean>(false);

  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState<boolean>(false);
  const [selectedSourcesFilter, setSelectedSourcesFilter] = useState<string[]>([
    ALL_SOURCES_OPTION,
  ]);

  const sources =
    useSelector(sourceSelectors.sourcesData)?.map((source) => {
      return {
        code: source.code,
        name: source.name,
        network: source.networkName,
      };
    }) || [];

  const [source, setSource] = useState<{ code: string; name: string }>({
    code: '',
    name: ALL_SOURCES_OPTION,
  });

  const loyaltyProducts = useSelector(loyaltySelectors.loyaltyProducts);

  const handleSetSource = (code: string, name: string) => {
    setSource({
      code,
      name,
    });
  };

  useEffect(() => {
    if (dataUser) {
      dispatch(
        sourceActions.getSourcesByNetwork.request({
          networkCode: dataUser?.networkCode,
        })
      );
    }
  }, [dispatch, dataUser]);

  useEffect(() => {
    if (startDate && endDate) setErrorTooltip(false);
  }, [startDate, endDate]);

  useEffect(() => {
    dispatch(actions.getLoyaltyProducts.request());
  }, [dispatch]);

  useEffect(() => {
    dispatch(actions.getLoyaltyPercentages.request());
  }, [dispatch]);

  const handleSentReport = () => {
    if (startDate && endDate && reportType) {
      dispatch(
        actions.generateLoyaltyReport.request({
          data: formatDataForLoyaltyReport(
            startDate,
            endDate,
            process.env.REACT_APP_INTEGRATION_NETWORK || 'apteka_so_sklada',
            source.code
          ),
          pdf: reportType === 'pdf',
          xls: reportType === 'xlsx',
        })
      );
    } else setErrorTooltip(true);
  };

  const handleChangeSources = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const normalized =
      typeof value === 'string' ? value.split(',') : (value as string[]);

    if (!normalized.length || normalized.length === sources.length) {
      setSelectedSourcesFilter([ALL_SOURCES_OPTION]);
      return;
    }
    if (normalized.includes(ALL_SOURCES_OPTION)) {
      setSelectedSourcesFilter(
        normalized[0] === ALL_SOURCES_OPTION
          ? normalized.filter((el: string) => el !== ALL_SOURCES_OPTION)
          : [ALL_SOURCES_OPTION]
      );
      return;
    }
    setSelectedSourcesFilter(normalized);
  };

  const handleSetReportType = (type: ReportType) => {
    setReportType(type);
  };

  const handleSubmitSkuBonus = useCallback(
    (payload: {
      sku: string;
      percent: number;
      network_code?: string;
      source_codes: string[];
    }) => {
      dispatch(
        actions.addLoyaltyProduct.request({
          ...payload,
        })
      );
    },
    []
  );

  const skuBonusRows = useMemo(
    () =>
      loyaltyProducts.map((product) => {
        const pharmacyName =
          sources.find((sourceItem) => sourceItem.code === product.source_code)
            ?.name ||
          product.source_code ||
          '-';

        return {
          sku: product.sku,
          pharmacy: pharmacyName,
          source_code: product.source_code,
          mapping_code: product.mapping_code,
          bonus: `${product.percent}%`,
          author: product.updated_by || product.mapping_code || '-',
        };
      }),
    [loyaltyProducts, sources]
  );

  return (
    <Styled.LoyaltyPageContainer>
      <h2>{t('LoyaltyPageReport.CURRENT_PARAMETERS')}</h2>

      <CurrentParametersTable />

      <h2>{t('LoyaltyPageReport.BONUSES_REPORT')}</h2>

      <Styled.PickersBlock>
        <NetworkSelector
          isOpen={sourcesOpen}
          onClose={() => setSourcesOpen(false)}
          onToggle={() => setSourcesOpen((prev) => !prev)}
          selectedName={source.name}
          sources={sources}
          onSelect={handleSetSource}
        />
        <PeriodPickerPanel
          startDate={startDate}
          endDate={endDate}
          onStartChange={setStartDate}
          onEndChange={setEndDate}
          showActions
          errorVisible={errorTooltip}
          onGenerate={handleSentReport}
          reportType={reportType}
          onReportTypeChange={handleSetReportType}
        />
      </Styled.PickersBlock>

      <h2>{t('LoyaltyPageReport.MANUAL_ADDITIONAL_BONUS')}</h2>

      <AdditionalBonuses sources={sources} />

      <h2>{t('LoyaltyPageReport.INCREASED_BONUS')}</h2>

      <Styled.ExpandedProductBonusBlock>
        <SkuBonusManager
          sources={sources}
          selectedSources={selectedSourcesFilter}
          onChangeSources={handleChangeSources}
          allSourcesLabel={ALL_SOURCES_OPTION}
          networkCode={source.code || dataUser?.networkCode}
          onSubmitBonus={handleSubmitSkuBonus}
          rows={skuBonusRows}
        />
      </Styled.ExpandedProductBonusBlock>
    </Styled.LoyaltyPageContainer>
  );
};

export default LoyaltyPageReport;
